import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, HostBinding, Input } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Observable } from 'rxjs';
import * as XlsxPopulate from 'xlsx-populate/browser/xlsx-populate.js';
import { ScrollContainerComponent } from '../../common/components/scroll.container/scroll.container.component';
import { FilterMultiComponent, FilterObject } from '../../common/filter';
import {
  ListOption,
  ScrollableListComponentBase,
} from '../../common/list.component.base';
import { DELETED, WebUtils } from '../../common/utils';
import { XlsUtils } from '../../common/xls.utils';
import {
  AccessUtils,
  TLocation,
  TOrganization,
  TSpace,
} from '../../core/entities';
import { SiteLangMixin } from '../../core/lang';
import { AppPermissions } from '../../core/permissions';
import { SitePathMixin } from '../../core/site.path.utils';
import { SessionCache } from '../../session.cache';
import { SessionService } from '../../session.service';
import { SiteFormatMixin } from '../../thinkdesks/site.format.mixin';
import { SpaceEditDialogComponent } from './space.edit.dialog';

class _Base extends ScrollableListComponentBase<TSpace> { }

@Component({
  selector: 'app-space-list',
  templateUrl: './space.list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FilterMultiComponent,
    ScrollContainerComponent,
    FontAwesomeModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    MatDialogModule,
    MatToolbarModule,
    MatTooltipModule,
  ],
})
export class SpaceListComponent extends SiteLangMixin(
  SitePathMixin(SiteFormatMixin(_Base))
) {
  _common = _common;
  @HostBinding('class') css = 'max-height';

  filterObject = new FilterObject();
  //   status = new FilterMulti();

  //   type = new FilterMulti();

  permissions = AppPermissions;
  displayedColumns: string[] = [
    //'active',
    'image',
    'name',
    //'organization',
    'location',
    'types',
    'capacity',
    'purpose',
    'locked',

    'controls',
  ];

  @Input()
  organization: TOrganization;

  @Input()
  location: TLocation;

  constructor(
    override http: HttpClient,
    override elRef: ElementRef,
    private dialog: MatDialog,
    override snackBar: MatSnackBar,
    private cache: SessionCache,
    public override session: SessionService
  ) {
    super(http, snackBar, elRef);
    // this.pageSort = 'seq';
    // this.pageSortDir = 'asc';
  }

  createBaseQuery() {
    let query = {
      organization: WebUtils.objId(this.organization),
      location: WebUtils.objId(this.location),
    } as any;
    Object.assign(query, this.filterObject.query());
    Object.assign(query, this.session.userQuery());
    return query;
  }
  override createRequest(options: ListOption): Observable<Object> {
    let query = this.createBaseQuery();

    const url = this._listModifyUrl(`/api/space/list`, {}, options);
    return this.http.post(url, query);
  }
  override ngOnInit() {
    super.ngOnInit();
    // this.filterObject.add('type', this.type);
    // this.filterObject.add('status', this.status);
  }
  override listFixList(list: TSpace[]) {
    for (let o of list) AccessUtils.fixSpace(o);
  }

  add() {
    const ref = this.dialog.open(SpaceEditDialogComponent, {
      data: {
        organization: this.organization,
        location: this.location,
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (result) {
        this._addToList(result);
      }
    });
  }

  override selectObject(o: TSpace) {
    const dialogRef = this.dialog.open(SpaceEditDialogComponent, {
      data: {
        objectId: o.id,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result == DELETED) {
        this._removeFromList(o);
      } else if (result) {
        Object.assign(o, result);
      }
    });
  }

  exportList() {
    let query = this.createBaseQuery();

    WebUtils.web_list(this.http.post(`/api/space/list`, query), (result) => {
      let list = result as any[];
      for (let o of list) {
        AccessUtils.fixSpace(o);
      }
      this.doExport(list);
    });
  }

  doExport(dataList: any[]) {
    XlsxPopulate.fromBlankAsync().then((workbook: XlsxPopulate.Workbook) => {
      const sheet = workbook.sheet('Sheet1');
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
      this.exportSheet(sheet, dataList);
      XlsUtils.webExport(workbook, null, {
        filename: `Space_list_${dateStr}.xlsx`,
      });
      // XlsUtils.webExport(workbook, null, {
      //   filename: `Space_list.xlsx`,
      // });
    });
  }

  exportSheet(sheet: XlsxPopulate.Sheet, dataList: any) {
    let columnOrder = [
      {
        path: 'createTime',
        name: 'Created at',
      },
      {
        path: 'createdBy.name',
        name: 'Created by (Backend User)',
      },
      {
        path: 'createdBy.userId',
        name: 'Created by (Backend User) (UUID)',
      },
      {
        path: 'createdMember.name',
        name: 'Created by (Frontend User)',
      },
      {
        path: 'createdMember.userId',
        name: 'Created by (Frontend User) (UUID)',
      },

      {
        path: 'active',
        name: 'Active',
      },
      {
        path: 'id',
        name: 'Space System ID',
      },
      {
        path: 'name',
        name: 'Space',
      },
      {
        path: 'location.organization.name',
        name: 'Organization (zh_hk)',
      },
      {
        path: 'location.organization.name_lang.en',
        name: 'Organization (en)',
      },
      {
        path: 'location.name',
        name: 'Center (zh_hk)',
      },
      {
        path: 'location.name_lang.en',
        name: 'Center (en)',
      },
      {
        path: 'autoApprove',
        name: 'Booking Auto Approve',
      },
      {
        path: 'description',
        name: 'Description',
      },
      {
        path: 'remarks',
        name: 'Remarks',
      },
      {
        path: 'capacity',
        name: 'Capacity',
      },
      {
        path: 'area',
        name: 'Venue Size',
      },
      {
        path: 'types',
        name: 'Category',
      },
      {
        path: 'includedFacilities',
        name: 'Facility included',
      },
      {
        path: 'facilities',
        name: 'Other Facilities',
      },
      {
        path: 'purpose',
        name: 'Theme',
      },
      {
        path: 'bookSchedule.name',
        name: 'Calendar',
      },
      {
        path: 'bookDays',
        name: 'Future Booking Allowed (days)',
      },
      {
        path: 'bookSlotIncrement',
        name: 'Increment',
      },
      {
        path: 'bookMinSlots',
        name: 'Min. Mins',
      },
      {
        path: 'bookMaxSlots',
        name: 'Max. Mins',
      },
      {
        path: 'terms',
        name: 'Booking Terms (Space)',
      },
      {
        path: 'locked',
        name: 'Smart Lock',
      },
      {
        path: 'maxBookings',
        name: 'Qty per slot',
      },
      {
        path: 'staffOnly',
        name: 'Staff Only',
      },
      
    ];

    const header = columnOrder.map((column) => column.name);
    const cells = [header];

    for (const space of dataList) {
      const data = columnOrder.map((column) => {
        const columnKeys = column.path.split('.');
        let cell = space;

        switch (column.path) {
          case 'active':
            return cell.active ? 'Y' : 'N';
          case 'locked':
            return cell.locked ? 'Y' : 'N';
          case 'autoApprove':
            return cell.autoApprove ? 'Y' : 'N';
          case 'staffOnly':
            return cell.staffOnly ? 'Y' : 'N';
            
          case 'facilities':
            return space.facilities
              .map((t) => this.lang(t.name, t.name_lang))
              .join(', ');
          case 'types':
            return space.types
              .map((t) => this.lang(t.name, t.name_lang))
              .join(', ');
          case 'purpose':
            return this.lang(cell.purpose?.name, cell.purpose?.name_lang);
          case 'description':
            return this.lang(cell.description, cell.description_lang);
          case 'remarks':
            return this.lang(cell.remarks, cell.remarks_lang);
          case 'createTime':
            return this.formatTime(cell.createTime);
          case 'bookSlotIncrement':
            return cell.bookMinSlots * 30;
          case 'bookMinSlots':
            return cell.bookMinSlots * 30;
          case 'bookMaxSlots':
            return cell.bookMinSlots * 30;
        }
        for (const columnKey of columnKeys) {
          if (!cell[columnKey]) return '';
          else cell = cell[columnKey];
        }
        return cell;
      });

      cells.push(data);
    }

    sheet.cell('A1').value(cells);
  }
}
